import { cva } from 'class-variance-authority';

/**
 * Tag class recipes.
 *
 * Ported from the Figma component set (file 5aSkE0U4TeWbuIZDvccHjc, node 597:43779) — 96 cells
 * across State x Colour x Mode x Deletable x Size, every value read off the exported vector.
 *
 * Like `Button.recipe`, this contains ZERO `dark:` variants: colour comes entirely from the
 * `--ds-tag-*` slots, which the `.ds-tag` / `.ds-tag-<colour>` classes in
 * `components/styles/design-system.css` set per theme.
 *
 * Sizing is height-based rather than padding-based, for the same reason `Button` is. Figma strokes
 * these INSIDE the shape, so the authored 32px cell with `py: 6` and a 20px line box only adds up if
 * the 1px border is painted within the height. A fixed height plus the app's global
 * `box-sizing: border-box` (`src/index.css`) reproduces that exactly; `py-[6px]` plus a border would
 * measure 34px.
 *
 * Heights, gaps and box sizes are measured off the file. HORIZONTAL PADDING IS NOT — the four values
 * below are design feedback on HNS-334, superseding the authored 16px (med) and 10px (sm). They are
 * asymmetric, and the right side depends on whether the tag is deletable, because the delete
 * control carries its own optical breathing room:
 *
 *                height   pl    pr    gap   icon   delete box   glyph
 *   med            32     12    12     4     16         -        -
 *   med + delete   32     12    10     4     16        20       12
 *   sm             20      8     8     4     14         -        -
 *   sm + delete    20      8     6     4     14        16       12
 *
 * That is why padding is a size x deletable compound rather than part of the `size` variant, and why
 * `deletable` is a variant distinct from `interactive`: a clickable tag is interactive but its
 * padding must not change.
 *
 * The authored radius is 30px, which clamps to fully-rounded at both heights, so `rounded-full` is
 * not an approximation.
 */
export const tagRecipe = cva(
  [
    'group/tag ds-tag',
    // `max-w-full` + the `min-w-0` chain below is what makes a long label truncate instead of
    // pushing the delete control out of the tag.
    'inline-flex max-w-full items-center gap-1',
    'rounded-full border border-solid',
    'font-sans text-[12px] leading-5 font-medium',
    'transition-colors duration-150 motion-reduce:transition-none',

    // Slot reads. The `.ds-tag-*` classes supply the values. `text` also colours the left icon,
    // which the design strokes in the label colour.
    'bg-[var(--ds-tag-bg)] border-[var(--ds-tag-border)] text-[var(--ds-tag-fg)]',

    // Disabled. `aria-disabled` rather than a `data-` attribute because the root is a plain
    // container, not a control — and because axe-core's colour-contrast rule skips text inside an
    // `aria-disabled` subtree, which is what keeps the 8 disabled cells (1.82:1 - 5.31:1, exempt
    // under WCAG 1.4.3 as inactive) from failing the a11y gate that the other 24 cells now pass.
    'aria-disabled:bg-[var(--ds-tag-bg-disabled)]',
    'aria-disabled:border-[var(--ds-tag-border-disabled)]',
    'aria-disabled:text-[var(--ds-tag-fg-disabled)]',
    // The icon follows the DISABLED label colour rather than staying at full-strength `dark/6`.
    // Inferred, not specified: the feedback that moved the light icon to a neutral does not mention
    // the disabled cells, and an undimmed icon on an otherwise faded tag reads as a bug.
    'aria-disabled:[--ds-tag-icon-fg:var(--ds-tag-fg-disabled)]',
  ],
  {
    variants: {
      size: {
        med: 'h-8 [--ds-tag-icon-size:16px]',
        sm: 'h-5 [--ds-tag-icon-size:14px]',
      },
      color: {
        neutral: 'ds-tag-neutral',
        primary: 'ds-tag-primary',
        success: 'ds-tag-success',
        destructive: 'ds-tag-destructive',
      },
      /**
       * Figma's `Hovered` state.
       *
       * The set draws hover on every cell, including the ones with nothing to click. Applying it
       * only when the tag actually has a control is deliberate: a plain tag is a piece of data, and
       * a colour change under the pointer on something inert reads as an affordance that isn't
       * there. The 24 non-deletable, non-clickable `Hovered` cells are therefore unreachable in
       * code — flagged to design rather than silently dropped.
       *
       * The `:not([aria-disabled=true])` guard makes these selectors disjoint from the disabled
       * ones above, so which rule wins never depends on utility sort order.
       */
      interactive: {
        true: [
          '[&:not([aria-disabled=true]):hover]:bg-[var(--ds-tag-bg-hover)]',
          '[&:not([aria-disabled=true]):hover]:border-[var(--ds-tag-border-hover)]',
          '[&:not([aria-disabled=true]):hover]:text-[var(--ds-tag-fg-hover)]',
        ],
        false: '',
      },
      /**
       * Carries the asymmetric right padding, and NOTHING else. Distinct from `interactive` on
       * purpose: `onClick` makes a tag interactive without adding a delete control, so it must not
       * shift the padding.
       */
      deletable: { true: '', false: '' },
    },
    compoundVariants: [
      // Horizontal padding — design feedback, not the authored Figma values. See the header table.
      { size: 'med', deletable: false, class: 'px-3' },
      { size: 'med', deletable: true, class: 'pr-2.5 pl-3' },
      { size: 'sm', deletable: false, class: 'px-2' },
      { size: 'sm', deletable: true, class: 'pr-1.5 pl-2' },
    ],
    defaultVariants: { color: 'neutral', size: 'med', interactive: false, deletable: false },
  }
);

/**
 * Undoes the user-agent's own button chrome.
 *
 * REQUIRED, not defensive. This layer deliberately omits Tailwind's preflight (see
 * `design-system.css`), so nothing else resets a native `<button>` — and both controls below are
 * real buttons. Without this, Chromium paints each one with `background: buttonface`
 * (rgb(239,239,239)), a `2px outset` black border and `1px 6px` of padding, and — because a button
 * does not inherit typography — renders its label in **Arial 13.33px/400** instead of the tag's
 * Google Sans Flex 12px/500. Measured in Chromium; every one of those numbers was observed, not
 * anticipated.
 *
 * `font: inherit` rather than a family/size/weight trio: it is the one declaration that covers the
 * whole inherited font, including line-height, so the label inside a clickable tag is typeset by
 * the ROOT and cannot drift from a plain tag's.
 *
 * `Button` in this same layer escapes the trap only by setting its background, border width and
 * font explicitly on every variant. A control that leaves any of them unset needs this list.
 */
const UA_BUTTON_RESET = ['m-0 appearance-none border-0 bg-transparent p-0', '[font:inherit]'];

/**
 * The icon + label group.
 *
 * A button when `onClick` is passed and a plain `span` otherwise, which is why the focus treatment
 * lives here rather than on the root: it must be reachable when this is a control and absent when
 * it is not. `rounded-full` matches the tag's own shape so the focus ring traces the tag rather than
 * a rectangle inside it.
 */
export const tagContentRecipe = cva(
  [
    ...UA_BUTTON_RESET,
    // The label takes the tag's colour; only the delete glyph has a colour of its own.
    'text-[color:inherit]',
    'inline-flex min-w-0 items-center gap-1 rounded-full',
    'text-left',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-primary',
  ],
  {
    variants: {
      interactive: {
        true: 'cursor-pointer [&[data-disabled]]:cursor-not-allowed',
        false: '',
      },
    },
    defaultVariants: { interactive: false },
  }
);

/**
 * The label itself.
 *
 * `min-w-0` is required in addition to the wrapper's: `truncate` can only shrink a flex item whose
 * minimum content size has been released, and there are two flex levels between here and the root.
 */
export const tagLabelRecipe = cva(['min-w-0 truncate']);

/**
 * Sizes and colours the `icon` slot. 16px at `med`, 14px at `sm`, tracked through one variable.
 *
 * The colour comes from `--ds-tag-icon-fg` rather than being inherited, because the icon is NOT the
 * label colour in light mode — see `design-system.css`. Setting `text` here means a caller's glyph
 * still just uses `currentColor` and needs to know nothing about it.
 */
export const tagIconRecipe = cva([
  'inline-flex shrink-0 items-center justify-center',
  'size-[var(--ds-tag-icon-size)] text-[var(--ds-tag-icon-fg)]',
  '[&_svg]:size-full',
]);

/**
 * The delete control.
 *
 * Its glyph colour comes from `--ds-tag-remove-fg`, which — unlike every other slot here — is set
 * on the shared `.ds-tag` class rather than per colour. That is not a shortcut: the design's × is a
 * mode-dependent NEUTRAL, identical across all four colours (`dark/1` on light, `light/0` on dark,
 * greying to `light/6` / `dark/7` when disabled). Setting it once encodes that finding instead of
 * restating the same pair four times.
 *
 * The design draws no hover or pressed cell for this control — hovering it changes the TAG, via
 * `tagRecipe`'s `interactive` variant, because the pointer is inside the root.
 */
export const tagRemoveRecipe = cva(
  [
    ...UA_BUTTON_RESET,
    'relative inline-flex shrink-0 items-center justify-center',
    'rounded-full text-[var(--ds-tag-remove-fg)] cursor-pointer',
    'transition-colors duration-150 motion-reduce:transition-none',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-primary',

    // WCAG 2.5.8 wants a 24px target; the design draws 20px at `med` and 16px at `sm`. A centred
    // pseudo-element grows the hit area to 24px without touching layout or paint. It is a child of
    // this button, so it cannot overlap the label's own hit area at `med`; at `sm` it reaches the
    // 4px gap exactly, and being later in paint order it wins there — a 4px overlap is a better
    // failure than a target nobody can hit.
    'after:absolute after:top-1/2 after:left-1/2 after:size-6 after:-translate-x-1/2',
    'after:-translate-y-1/2 after:content-[""]',

    'group-aria-disabled/tag:text-[var(--ds-tag-remove-fg-disabled)]',
    '[&[data-disabled]]:cursor-not-allowed',
  ],
  {
    variants: {
      size: { med: 'size-5', sm: 'size-4' },
    },
    defaultVariants: { size: 'med' },
  }
);

/** The × itself. 12px at BOTH sizes — the delete box shrinks at `sm`, the glyph does not. */
export const tagRemoveIconRecipe = cva(['size-3 shrink-0']);
